import { useState, useEffect, useRef } from "react";
import { getDatabase, ref, onValue, off } from "firebase/database";
import firebaseApp from "../firebaseConfig";
import {
  normalizeUserData,
  checkUserAvailability,
  checkVersionCompatibility,
} from "../utils/FirebaseDataUtils";

/**
 * Presence data is stamped with the userId it arrived for, and read back
 * through this guard. Switching clients otherwise renders the previous
 * client's devices and callStatus under the new client's name until Firebase
 * calls back — clearing inside the effect leaves a render where the stale data
 * is still returned, because state updates are async (#489 review).
 *
 * Ids arrive as strings from route params and as numbers from context, so the
 * comparison is on the string form deliberately.
 *
 * @template T
 * @param {(T & { userId: string|number })|null|undefined} snapshot
 * @param {string|number|null|undefined} userId
 * @returns {T|null}
 */
export function presenceForUser(snapshot, userId) {
  if (!snapshot || userId === null || userId === undefined) return null;
  return String(snapshot.userId) === String(userId) ? snapshot : null;
}

let loggedDatabaseUnavailable = false;

/**
 * `getDatabase` throws when the Firebase app has not been initialized (no
 * config, offline bootstrap, a test that does not stub it). These hooks are
 * called at the top level of screens whose primary content has nothing to do
 * with presence — since #489 that includes the client profile — so letting the
 * throw escape blanks the whole page over a decorative status indicator.
 * Degrade to "no presence data" instead; every consumer already renders an
 * offline/absent state.
 * @returns {import("firebase/database").Database|null}
 */
function getPresenceDatabase() {
  try {
    return getDatabase(firebaseApp);
  } catch (error) {
    // Called on every render of every presence consumer, so log once per
    // session rather than once per render — an outage would otherwise bury
    // the console under thousands of identical lines.
    if (!loggedDatabaseUnavailable) {
      loggedDatabaseUnavailable = true;
      console.error("🔴 [PRESENCE] Firebase database unavailable:", error);
    }
    return null;
  }
}

export const useUserPresence = (userId, options = {}) => {
  const {
    minVersion = "1.0.0",
    enableVersionCheck = true,
    enableAvailabilityCheck = true,
    onStatusChange = null,
  } = options;

  const [userData, setUserData] = useState(null);
  const [devicesMap, setDevicesMap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const database = getPresenceDatabase();
  const listenerRef = useRef(null);
  const devicesListenerRef = useRef(null);
  const prevStatusRef = useRef(null);

  useEffect(() => {
    console.log(
      `🟢 [PRESENCE] useUserPresence hook called with userId: "${userId}" (type: ${typeof userId})`
    );

    if (!userId) {
      console.log(`🟢 [PRESENCE] No userId provided, returning early`);
      setUserData(null);
      setDevicesMap(null);
      setLoading(false);
      setError("No user ID provided");
      return;
    }

    if (!database) {
      setUserData(null);
      setDevicesMap(null);
      setLoading(false);
      setError("Firebase database unavailable");
      return;
    }

    setLoading(true);
    setError(null);

    const path = `status/${userId}`;
    const devicesPath = `status/${userId}/devices`;
    console.log(`🟢 [PRESENCE] Setting up Firebase listener at path: /${path}`);
    const userRef = ref(database, path);
    const devicesRef = ref(database, devicesPath);

    let statusLoaded = false;
    let devicesLoaded = false;

    const maybeDoneLoading = () => {
      if (statusLoaded && devicesLoaded) {
        setLoading(false);
      }
    };

    const handleData = (snapshot) => {
      try {
        const rawData = snapshot.val();
        console.log(
          `🟢 [PRESENCE] Firebase data received for userId ${userId} (action=presence_snapshot):`,
          {
            exists: snapshot.exists(),
            rawData,
          }
        );
        const normalized = normalizeUserData(rawData);

        // Add computed fields
        const availability = enableAvailabilityCheck
          ? checkUserAvailability(normalized)
          : null;
        const versionCheck = enableVersionCheck
          ? checkVersionCompatibility(normalized.appVersion, minVersion)
          : null;

        const enrichedData = {
          ...normalized,
          userId,
          exists: snapshot.exists(),
          availability,
          versionCheck,
          lastUpdate: new Date().toISOString(),
        };

        setUserData(enrichedData);
        setLastUpdate(new Date().toISOString());
        statusLoaded = true;
        maybeDoneLoading();
        setError(null);

        // Call status change callback
        if (onStatusChange && prevStatusRef.current !== normalized.callStatus) {
          onStatusChange(
            normalized.callStatus,
            prevStatusRef.current,
            enrichedData
          );
          prevStatusRef.current = normalized.callStatus;
        }

        console.log(`👤 [PRESENCE] User ${userId} status updated (action=presence_snapshot):`, {
          callStatus: normalized.callStatus,
          isOnline: normalized.isOnline,
          platform: normalized.platform,
          appVersion: normalized.appVersion,
        });
      } catch (err) {
        console.error(
          `❌ [PRESENCE] Error processing user ${userId} data (action=presence_snapshot):`,
          err
        );
        setError(`Error processing user data: ${err.message}`);
        statusLoaded = true;
        maybeDoneLoading();
      }
    };

    const handleDevicesData = (snapshot) => {
      try {
        // Stamped with the client it arrived for so a client switch cannot
        // render the previous client's devices (#489 review).
        setDevicesMap({
          userId,
          map: snapshot.exists() ? snapshot.val() : null,
        });
        devicesLoaded = true;
        maybeDoneLoading();
      } catch (err) {
        console.error(
          `❌ [PRESENCE] Error processing devices for user ${userId} (action=presence_snapshot):`,
          err,
        );
        setDevicesMap(null);
        devicesLoaded = true;
        maybeDoneLoading();
      }
    };

    const handleError = (err) => {
      console.error(`❌ [PRESENCE] Firebase error for user ${userId} (action=presence_snapshot):`, err);
      setError(`Firebase error: ${err.message}`);
      statusLoaded = true;
      devicesLoaded = true;
      setLoading(false);
    };

    const handleDevicesError = (err) => {
      console.error(
        `❌ [PRESENCE] Firebase devices error for user ${userId} (action=presence_snapshot):`,
        err,
      );
      setDevicesMap(null);
      devicesLoaded = true;
      maybeDoneLoading();
    };

    // Set up listener
    listenerRef.current = onValue(userRef, handleData, handleError);
    devicesListenerRef.current = onValue(
      devicesRef,
      handleDevicesData,
      handleDevicesError,
    );

    // Cleanup — onValue() (Firebase v9 modular) returns an UNSUBSCRIBE
    // function, not the callback. Call it to detach. Passing it to
    // off(ref, "value", fn) would not remove the listener (fn is the
    // unsubscribe, not the original callback), leaking listeners and causing
    // duplicate updates on re-run/unmount. Matches useMultipleUserPresence.
    return () => {
      if (typeof listenerRef.current === "function") {
        listenerRef.current();
      }
      if (typeof devicesListenerRef.current === "function") {
        devicesListenerRef.current();
      }
    };
  }, [
    userId,
    minVersion,
    enableVersionCheck,
    enableAvailabilityCheck,
    onStatusChange,
  ]);

  // Read both payloads through the guard: state survives a userId change, so
  // without this the hook returns the previous client's data for at least one
  // render (#489 review).
  const scopedUserData = presenceForUser(userData, userId);
  const scopedDevices = presenceForUser(devicesMap, userId);

  return {
    userData: scopedUserData,
    loading,
    error,
    lastUpdate,
    isOnline: scopedUserData?.isOnline || false,
    callStatus: scopedUserData?.callStatus || "unknown",
    platform: scopedUserData?.platform || "unknown",
    appVersion: scopedUserData?.appVersion || "Unknown",
    devicesMap: scopedDevices ? scopedDevices.map : null,
    availability: scopedUserData?.availability || null,
    versionCheck: scopedUserData?.versionCheck || null,
  };
};

/**
 * Hook for tracking multiple users' presence
 * @param {Array<string>} userIds - Array of user IDs to track
 * @param {Object} options - Configuration options
 * @returns {Object} Multiple users' presence data
 */
export const useMultipleUserPresence = (userIds = [], options = {}) => {
  const {
    minVersion = "1.0.0",
    enableVersionCheck = true,
    enableAvailabilityCheck = true,
    onUserStatusChange = null,
  } = options;

  const [usersData, setUsersData] = useState({});
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [lastUpdate, setLastUpdate] = useState(null);

  const database = getPresenceDatabase();
  const listenersRef = useRef({});

  useEffect(() => {
    if (!userIds.length) {
      setUsersData({});
      setLoading(false);
      setErrors({});
      return;
    }

    if (!database) {
      setUsersData({});
      setLoading(false);
      return;
    }

    setLoading(true);

    // Clean up existing listeners
    Object.values(listenersRef.current).forEach((unsubscribe) => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    });
    listenersRef.current = {};

    const newUsersData = {};
    const newErrors = {};
    let loadedCount = 0;

    userIds.forEach((userId) => {
      const userRef = ref(database, `status/${userId}`);

      const handleData = (snapshot) => {
        try {
          const rawData = snapshot.val();
          const normalized = normalizeUserData(rawData);

          // Add computed fields
          const availability = enableAvailabilityCheck
            ? checkUserAvailability(normalized)
            : null;
          const versionCheck = enableVersionCheck
            ? checkVersionCompatibility(normalized.appVersion, minVersion)
            : null;

          const enrichedData = {
            ...normalized,
            userId,
            exists: snapshot.exists(),
            availability,
            versionCheck,
            lastUpdate: new Date().toISOString(),
          };

          newUsersData[userId] = enrichedData;
          delete newErrors[userId];

          setUsersData((prev) => ({ ...prev, [userId]: enrichedData }));
          setErrors((prev) => {
            const updated = { ...prev };
            delete updated[userId];
            return updated;
          });

          loadedCount++;
          if (loadedCount >= userIds.length) {
            setLoading(false);
          }

          setLastUpdate(new Date().toISOString());

          // Call status change callback
          if (onUserStatusChange) {
            onUserStatusChange(userId, normalized.callStatus, enrichedData);
          }
        } catch (err) {
          console.error(
            `❌ [PRESENCE] Error processing user ${userId} data (action=presence_snapshot):`,
            err
          );
          newErrors[userId] = `Error processing data: ${err.message}`;
          setErrors((prev) => ({ ...prev, [userId]: newErrors[userId] }));

          loadedCount++;
          if (loadedCount >= userIds.length) {
            setLoading(false);
          }
        }
      };

      const handleError = (err) => {
        console.error(`❌ [PRESENCE] Firebase error for user ${userId} (action=presence_snapshot):`, err);
        newErrors[userId] = `Firebase error: ${err.message}`;
        setErrors((prev) => ({ ...prev, [userId]: newErrors[userId] }));

        loadedCount++;
        if (loadedCount >= userIds.length) {
          setLoading(false);
        }
      };

      // Set up listener
      listenersRef.current[userId] = onValue(userRef, handleData, handleError);
    });

    // Cleanup
    return () => {
      Object.values(listenersRef.current).forEach((unsubscribe) => {
        if (typeof unsubscribe === "function") {
          unsubscribe();
        }
      });
    };
  }, [
    userIds.join(","),
    minVersion,
    enableVersionCheck,
    enableAvailabilityCheck,
    onUserStatusChange,
  ]);

  return {
    usersData,
    loading,
    errors,
    lastUpdate,
    // Convenience methods
    getUserData: (userId) => usersData[userId] || null,
    isUserOnline: (userId) => usersData[userId]?.isOnline || false,
    getUserCallStatus: (userId) => usersData[userId]?.callStatus || "unknown",
    getUserAvailability: (userId) => usersData[userId]?.availability || null,
    getOnlineUsers: () =>
      Object.values(usersData).filter((user) => user.isOnline),
    getAvailableUsers: () =>
      Object.values(usersData).filter((user) => user.availability?.available),
    getUsersByPlatform: (platform) =>
      Object.values(usersData).filter((user) => user.platform === platform),
  };
};

/**
 * Hook for tracking all users in the status node
 * @param {Object} options - Configuration options
 * @returns {Object} All users' presence data
 */
export const useAllUsersPresence = (options = {}) => {
  const {
    minVersion = "1.0.0",
    enableVersionCheck = true,
    enableAvailabilityCheck = true,
    _onUserStatusChange = null, // Prefixed with _ to indicate intentionally unused (for future use)
    filterOfflineUsers = false,
  } = options;

  const [allUsersData, setAllUsersData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [userCount, setUserCount] = useState(0);

  const database = getPresenceDatabase();
  const listenerRef = useRef(null);

  useEffect(() => {
    if (!database) {
      setLoading(false);
      setError("Firebase database unavailable");
      return;
    }

    setLoading(true);
    setError(null);

    const statusRef = ref(database, "status");

    const handleData = (snapshot) => {
      try {
        const rawData = snapshot.val() || {};
        const processedData = {};
        let count = 0;

        Object.entries(rawData).forEach(([userId, userData]) => {
          const normalized = normalizeUserData(userData);

          // Filter offline users if requested
          if (filterOfflineUsers && !normalized.isOnline) {
            return;
          }

          // Add computed fields
          const availability = enableAvailabilityCheck
            ? checkUserAvailability(normalized)
            : null;
          const versionCheck = enableVersionCheck
            ? checkVersionCompatibility(normalized.appVersion, minVersion)
            : null;

          processedData[userId] = {
            ...normalized,
            userId,
            availability,
            versionCheck,
            lastUpdate: new Date().toISOString(),
          };

          count++;
        });

        setAllUsersData(processedData);
        setUserCount(count);
        setLastUpdate(new Date().toISOString());
        setLoading(false);
        setError(null);

        console.log(
          `👥 [PRESENCE] All users status updated (action=presence_snapshot): ${count} users tracked`
        );
      } catch (err) {
        console.error("❌ [PRESENCE] Error processing all users data (action=presence_snapshot):", err);
        setError(`Error processing users data: ${err.message}`);
        setLoading(false);
      }
    };

    const handleError = (err) => {
      console.error("❌ [PRESENCE] Firebase error for all users (action=presence_snapshot):", err);
      setError(`Firebase error: ${err.message}`);
      setLoading(false);
    };

    // Set up listener
    listenerRef.current = onValue(statusRef, handleData, handleError);

    // Cleanup
    return () => {
      if (listenerRef.current) {
        off(statusRef, "value", listenerRef.current);
      }
    };
  }, [
    minVersion,
    enableVersionCheck,
    enableAvailabilityCheck,
    filterOfflineUsers,
  ]);

  return {
    allUsersData,
    loading,
    error,
    lastUpdate,
    userCount,
    // Convenience methods
    getOnlineUsers: () =>
      Object.values(allUsersData).filter((user) => user.isOnline),
    getAvailableUsers: () =>
      Object.values(allUsersData).filter(
        (user) => user.availability?.available
      ),
    getBusyUsers: () =>
      Object.values(allUsersData).filter((user) => user.callStatus === "busy"),
    getUsersByPlatform: (platform) =>
      Object.values(allUsersData).filter((user) => user.platform === platform),
    getOutdatedUsers: () =>
      Object.values(allUsersData).filter(
        (user) => user.versionCheck && !user.versionCheck.compatible
      ),
  };
};

export default {
  useUserPresence,
  useMultipleUserPresence,
  useAllUsersPresence,
};
